import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import { resolveIconNode, ModalClose } from '../../components/icons'

const KPI_ICONS = [
  { key: 'activeDeals', icon: 'collaborate', cls: 'blue' },
  { key: 'totalTargetAum', icon: 'check', cls: 'green' },
  { key: 'avgConversion', icon: 'chart', cls: 'purple', suffix: '%' },
  { key: 'avgCycleDays', icon: 'clock', cls: 'cyan', suffix: 'd' },
  { key: 'stalledDeals', icon: 'alertTriangle', cls: 'orange' },
  { key: 'urgentAttention', icon: 'alert', cls: 'red' },
]

const KPI_LABELS = {
  activeDeals: 'Active Deals',
  totalTargetAum: 'Total Target AUM',
  avgConversion: 'Avg Conversion',
  avgCycleDays: 'Avg Cycle Time',
  stalledDeals: 'Stalled Deals',
  urgentAttention: 'Urgent Attention',
}

const STAGE_BORDER = ['s1', 's2', 's3', 's4', 's5', 's6']

const PAGE_SIZE = 7

function sizeBucket(amount) {
  const n = Number(amount) || 0
  if (n >= 200e6) return 'large'
  if (n >= 50e6) return 'medium'
  return 'small'
}

export default function AdminPipeline() {
  const [data, setData] = useState({ kpis: {}, stages: [], deals: [] })
  const [stageFilter, setStageFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sizeFilter, setSizeFilter] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [detailDeal, setDetailDeal] = useState(null)

  useEffect(() => {
    axios.get('/api/admin/pipeline')
      .then((r) => setData(r.data))
      .catch(() => toast.error('\u52a0\u8f7d\u5931\u8d25'))
  }, [])

  const filtered = useMemo(() => (data.deals || []).filter((d) => {
    if (stageFilter && d.stage_key !== stageFilter) return false
    if (statusFilter && d.pipeline_status !== statusFilter) return false
    if (sizeFilter && sizeBucket(d.target_amount) !== sizeFilter) return false
    if (search) {
      const q = search.toLowerCase()
      if (!d.title?.toLowerCase().includes(q)
        && !d.gp_name?.toLowerCase().includes(q)
        && !d.gp_company?.toLowerCase().includes(q)) return false
    }
    return true
  }), [data.deals, stageFilter, statusFilter, sizeFilter, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [stageFilter, statusFilter, sizeFilter, search])

  const resetFilters = () => {
    setStageFilter('')
    setStatusFilter('')
    setSizeFilter('')
    setSearch('')
  }

  const kpis = data.kpis || {}

  return (
    <AdminLayout title="Fundraising Pipeline">
      <div className="admin-kpi-row">
        {KPI_ICONS.map(({ key, icon, cls, suffix }) => (
          <div key={key} className="admin-kpi-card admin-kpi-card-rich">
            <div className="admin-kpi-header">
              <span className={`admin-kpi-icon ${cls}`}>{resolveIconNode(icon, { size: 'md' })}</span>
            </div>
            <div className="admin-kpi-value">
              {kpis[key] ?? 0}{suffix || ''}
            </div>
            <div className="admin-kpi-label">{KPI_LABELS[key]}</div>
          </div>
        ))}
      </div>

      <div className="admin-stage-row">
        {(data.stages || []).map((s, i) => (
          <div key={s.key} className={`admin-stage-card ${STAGE_BORDER[i] || 's1'}`}>
            <div className="admin-stage-num">Stage {s.num}</div>
            <div className="admin-stage-name">{s.name}</div>
            <div className="admin-stage-count">{s.count}</div>
            <div className="admin-stage-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">Active Fundraising Pipeline</span>
        </div>
        <div className="admin-filter-row">
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
            <option value="">All Stages</option>
            <option value="nda">NDA</option>
            <option value="dataroom">Dataroom</option>
            <option value="dd">Due Diligence</option>
            <option value="ts">Term Sheet</option>
            <option value="signing">Signing</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="stalled">Stalled</option>
            <option value="urgent">Urgent</option>
            <option value="closing">Closing</option>
          </select>
          <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}>
            <option value="">All Sizes</option>
            <option value="small">&lt; $50M</option>
            <option value="medium">$50M - $200M</option>
            <option value="large">&gt; $200M</option>
          </select>
          <input type="text" placeholder="Search deal or GP..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={resetFilters}>Reset</button>
        </div>
        <table className="admin-data-table">
          <thead>
            <tr>
              <th>Deal</th>
              <th>Target AUM</th>
              <th>Stage</th>
              <th>LPs Engaged</th>
              <th>Progress</th>
              <th>Days Active</th>
              <th>Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((d) => (
              <tr key={d.id}>
                <td>
                  <div className="admin-deal-cell">
                    <span className="deal-name">{d.title}</span>
                    <span className="deal-gp">{d.gp_label}</span>
                  </div>
                </td>
                <td><span style={{ fontWeight: 700 }}>{d.target_display}</span></td>
                <td>
                  <span className={`admin-stage-badge ${d.stage_key}`}>
                    {d.stage_label}
                  </span>
                </td>
                <td>
                  <span style={{ fontWeight: 600 }}>{d.lp_engaged_label || d.lp_count || 0}</span>
                </td>
                <td>
                  <div className="admin-progress-bar">
                    <div className={`admin-progress-fill p${d.progress}`} style={{ width: `${d.progress}%` }} />
                  </div>
                </td>
                <td><span style={{ fontWeight: 600 }}>{d.days_active}d</span></td>
                <td>
                  {d.pipeline_status === 'closing' ? (
                    <span style={{ color: '#059669', fontWeight: 600 }}>Closing</span>
                  ) : (
                    <>
                      <span className={`admin-status-dot-inline ${d.pipeline_status}`} />
                      <span style={{ color: d.pipeline_status_color, fontWeight: 600 }}>{d.pipeline_status_label}</span>
                    </>
                  )}
                </td>
                <td className="admin-td-actions">
                  <div className="admin-action-btns">
                  <button type="button" className="admin-action-btn view" onClick={() => setDetailDeal(d)}>View</button>
                  </div>
                </td>
              </tr>
            ))}
            {!pageItems.length && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-muted)' }}>No deals match filters</td></tr>
            )}
          </tbody>
        </table>
        <div className="admin-pagination">
          <div className="admin-pagination-info">
            Showing {filtered.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} active deals
          </div>
          <div className="admin-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      {detailDeal && (
        <div className="admin-modal-overlay show" role="presentation" onClick={() => setDetailDeal(null)}>
          <div className="admin-modal admin-modal-wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>
                <h3>{detailDeal.title}</h3>
                <p className="admin-modal-subtitle">{detailDeal.gp_label}</p>
              </div>
              <ModalClose onClick={() => setDetailDeal(null)} />
            </div>
            <div className="admin-modal-body">
              <div className="admin-review-meta-grid">
                <div className="admin-review-meta-block">
                  <p><strong>Target AUM:</strong> {detailDeal.target_display}</p>
                  <p><strong>Stage:</strong> {detailDeal.stage_label}</p>
                  <p><strong>Progress:</strong> {detailDeal.progress}%</p>
                </div>
                <div className="admin-review-meta-block">
                  <p><strong>LPs engaged:</strong> {detailDeal.lp_engaged_label || detailDeal.lp_count || 0}</p>
                  <p><strong>Days active:</strong> {detailDeal.days_active}d</p>
                  <p><strong>Status:</strong> {detailDeal.pipeline_status_label}</p>
                </div>
                <div className="admin-review-meta-block">
                  <p><strong>Industry:</strong> {detailDeal.industry || '—'}</p>
                  <p><strong>GP:</strong> {detailDeal.gp_name || '—'}</p>
                  <p><strong>Company:</strong> {detailDeal.gp_company || '—'}</p>
                </div>
                <div className="admin-review-meta-block">
                  <p><strong>Phase:</strong> {detailDeal.phase || '—'}</p>
                  <p><strong>Created:</strong> {detailDeal.created_at ? new Date(detailDeal.created_at).toLocaleDateString() : '—'}</p>
                  <p><strong>Updated:</strong> {detailDeal.updated_at ? new Date(detailDeal.updated_at).toLocaleDateString() : '—'}</p>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={() => setDetailDeal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
